// src/components/common/TaxExemptionBanner.js — taxexempt3-8c4f2a1d (tax codes on every line, Stage S3)
//
// The staff banners about a customer's tax exemption on an estimate, job or invoice VIEW page
// (mockup "Catalogue Phase 1 — customer tax exemption"):
//   blue  — "This customer is exempt from GST (#…). It isn’t charged on this document." + (office roles)
//           "Charge tax on this document", which asks for a reason (required).
//   amber — "Exemption switched off for this document by Sierra, Oct 30 — “reason”" + (office roles)
//           "Apply exemption again".
// Plus two office-only marks: the QuickBooks hold (no QuickBooks code for what was charged) and
// "tax couldn't be checked when this was made". Shown only when exemptions are on for the company
// (lineTax.exemptionsSwitchOn) — except those two marks, which exist only when the server set them.
// Technicians and Sales Consultants see the banner, never the buttons; the server refuses them anyway.
// NEVER on anything a customer sees.
//
// taxpick4-54573069 — Stage S4: also on the estimate EDIT page. There `document` is the page's preview (what
// saving will store), `blockedReason` turns the two buttons off with a reason ("Save your changes first" —
// the server re-totals the SAVED estimate), and `onFresh(data)` hands the re-read estimate to the editor.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Info, AlertTriangle } from 'lucide-react';
import { documentExemptionState, jobExemptionState, exemptionBannerText, EXEMPTION_REASON_MAX } from '../../utils/lineTax';
import {
    useTaxCodesDoc, exemptionsOnFor, canChangeTaxExemption, setDocumentTaxExemptionCall, shortDate,
    qbTaxCodeHoldOf, taxCheckNeededOf, TAX_CHECK_TEXT, callErrorText, withExemptionChange, EXEMPTION_COLLECTIONS,
} from '../../utils/taxExemptionUi';

// taxexempt3-8c4f2a1d — the view pages get their document once, as a prop, from screens this stage doesn't
// own. withExemptionRefresh wraps a page: when the banner's server call succeeds, the banner re-reads the
// document and hands it here, and the page is shown with the server's new figures at once (no reopening).
// A newer copy of the document from the parent (a different document, or a later save) replaces ours.
const ExemptionRefreshContext = createContext(null);
const stampOf = (d) => { const u = d && d.updatedAt; return u && typeof u.toMillis === 'function' ? u.toMillis() : String(u || ''); };

export function withExemptionRefresh(Component, propName) {
    function WithExemptionRefresh(props) {
        const original = props[propName];
        const [fresh, setFresh] = useState(null);
        const id = original ? original.id : null;
        const stamp = stampOf(original);
        useEffect(() => { setFresh(null); }, [id, stamp]);
        const shown = useMemo(() => withExemptionChange(original, fresh), [original, fresh]);
        return (
            <ExemptionRefreshContext.Provider value={setFresh}>
                <Component {...props} {...{ [propName]: shown }} />
            </ExemptionRefreshContext.Provider>
        );
    }
    WithExemptionRefresh.displayName = `WithExemptionRefresh(${Component.displayName || Component.name || 'Page'})`;
    return WithExemptionRefresh;
}

const LINK = 'text-[#5F4EC9] font-semibold text-sm hover:underline disabled:opacity-50 min-h-[40px] px-1';

export default function TaxExemptionBanner({ kind, document: d, customer, orgId, currentUserProfile, onChanged, blockedReason, onFresh }) {
    const taxCodesDoc = useTaxCodesDoc(orgId);
    const [asking, setAsking] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const showFresh = useContext(ExemptionRefreshContext);
    const on = exemptionsOnFor(orgId, taxCodesDoc);
    // A job carries no tax rows: its banner comes from the customer (read only when exemptions are on).
    const [jobCustomer, setJobCustomer] = useState(null);
    const clientId = kind === 'job' && !customer && d ? d.clientId : null;
    useEffect(() => {
        if (!on || !orgId || !clientId) { setJobCustomer(null); return undefined; }
        return onSnapshot(doc(db, `organizations/${orgId}/customers/${clientId}`),
            (snap) => setJobCustomer(snap.exists() ? snap.data() : null), () => setJobCustomer(null));
    }, [on, orgId, clientId]);
    if (!d) return null;

    const office = canChangeTaxExemption(currentUserProfile);
    const state = !on ? null : (kind === 'job' ? jobExemptionState(d, customer || jobCustomer) : documentExemptionState(d));
    const hold = kind === 'invoice' && office ? qbTaxCodeHoldOf(d) : null;
    const check = office ? taxCheckNeededOf(d) : null;
    if (!state && !hold && !check && !done) return null;

    const call = async (off) => {
        setBusy(true); setError('');
        try {
            await setDocumentTaxExemptionCall({ kind, id: d.id, off, ...(off ? { reason: reason.trim() } : {}) });
        } catch (e) {
            setError(callErrorText(e)); // nothing changed on the server, so nothing changes here
            setBusy(false);
            return;
        }
        setAsking(false); setReason('');
        const what = off ? 'Tax is now charged on this document.' : 'The exemption applies to this document again.';
        let shownNew = false;
        try { // the document as the server now has it — new banner, tax rows and total
            const snap = await getDoc(doc(db, `organizations/${orgId}/${EXEMPTION_COLLECTIONS[kind]}/${d.id}`));
            if (snap.exists() && showFresh) { showFresh({ id: d.id, data: snap.data() }); shownNew = true; }
            else if (snap.exists() && onFresh) { onFresh({ id: d.id, ...snap.data() }); shownNew = true; } // taxpick4-54573069 — the edit page
        } catch (e) {
            console.error('TaxExemptionBanner: saved, but could not re-read the document', e);
        }
        setDone(shownNew ? `Saved. ${what}` : `Saved. ${what} Close and reopen this page to see the new figures.`);
        setBusy(false);
        if (onChanged) onChanged();
    };

    return (
        <div className="space-y-2 mb-4 no-print" data-testid="tax-exemption-banners">
            {check && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="tax-check-needed">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" /><span>{TAX_CHECK_TEXT}</span>
                </div>
            )}
            {hold && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="qb-tax-code-hold">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" /><span>{hold.message}</span>
                </div>
            )}
            {state && state.kind !== 'off' && !asking && (
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-gray-900" data-testid="tax-exemption-applied">
                    <span className="flex items-start gap-2"><Info size={16} className="mt-0.5 shrink-0 text-blue-700" aria-hidden="true" />{exemptionBannerText(state)}</span>
                    {office && <button type="button" className={LINK} onClick={() => { setAsking(true); setDone(''); setError(''); }} disabled={busy || !!blockedReason} title={blockedReason || undefined}>Charge tax on this document</button>}
                </div>
            )}
            {state && state.kind !== 'off' && asking && (
                <div className="p-3 rounded-xl border-2 border-[#5F4EC9] bg-white text-sm space-y-2" data-testid="tax-exemption-dialog">
                    <p className="font-bold">Charge tax on this document?</p>
                    <p className="text-gray-700">Only this document changes. The customer stays exempt everywhere else. Your name and reason are saved on the document.</p>
                    <label htmlFor="tax-exemption-reason" className="block text-xs font-semibold">Reason <span className="text-red-700">· Required</span></label>
                    <input id="tax-exemption-reason" value={reason} maxLength={EXEMPTION_REASON_MAX} onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Work done off reserve" className="w-full min-h-[44px] px-3 border border-gray-300 rounded-lg" />
                    {error && <p className="text-red-700 text-xs" role="alert">{error}</p>}
                    <div className="flex justify-end gap-2">
                        <button type="button" className="min-h-[44px] px-4 border border-gray-300 rounded-lg font-semibold" onClick={() => { setAsking(false); setReason(''); setError(''); }} disabled={busy}>Cancel</button>
                        <button type="button" className="min-h-[44px] px-4 rounded-lg font-bold text-white bg-[#5F4EC9] disabled:bg-[#a5a0d6]" onClick={() => call(true)} disabled={busy || !reason.trim()}>{busy ? 'Saving…' : 'Charge tax'}</button>
                    </div>
                </div>
            )}
            {state && state.kind === 'off' && (
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="tax-exemption-off">
                    <span>{exemptionBannerText(state, shortDate(state.off.at))}</span>
                    {office && <button type="button" className={LINK} onClick={() => call(false)} disabled={busy || !!blockedReason} title={blockedReason || undefined}>{busy ? 'Saving…' : 'Apply exemption again'}</button>}
                </div>
            )}
            {office && blockedReason && state && !asking && <p className="text-xs text-gray-500">{blockedReason}</p>}
            {!asking && error && <p className="text-red-700 text-xs" role="alert">{error}</p>}
            {done && <p className="text-xs text-gray-600" role="status">{done}</p>}
        </div>
    );
}
